import {
  type HTMLAttributes,
  type ReactNode,
  forwardRef,
  useEffect,
  useId,
  useState,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import CloseIcon from '../../../assets/icons/General/close.svg?react';
import AlertCircleIcon from '../../../assets/icons/General/alert-circle.svg?react';
import InfoIcon from '../../../assets/icons/General/info.svg?react';
import CheckIcon from '../../../assets/icons/General/check.svg?react';
import { Typography } from '../Typography';
import { ChevronBottomIcon } from '../../../assets/icons/ChevronBottomIcon';

const alertContainerStyles = cva(
  'relative w-full gap-4 rounded-3xl border py-2 pl-2 pr-4',
  {
    variants: {
      variant: {
        info: 'border-blue-200 bg-blue-25 text-blue-800',
        success: 'border-green-200 bg-green-25 text-green-800',
        warning: 'border-yellow-300 bg-yellow-50 text-yellow-800',
        danger: 'border-red-200 bg-red-25 text-red-800',
        neutral: 'border-black-200 bg-black-25 text-black-800',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
);

const detailedViewWrapperStyles = cva(
  'flex flex-row items-center justify-start gap-3 rounded-3xl bg-blue-100 px-3 pr-3 pl-1',
  {
    variants: {
      variant: {
        info: 'bg-blue-100',
        success: 'bg-green-100',
        warning: 'bg-yellow-100',
        danger: 'bg-red-100',
        neutral: 'bg-black-100',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
);

const detailedViewLabelWrapperStyles = cva(
  'rounded-3xl font-medium px-3 py-1 my-1 text-sm',
  {
    variants: {
      variant: {
        info: 'bg-blue-900 text-white',
        success: 'bg-green-600 text-white',
        warning: 'bg-yellow-600 text-white',
        danger: 'bg-red-600 text-white',
        neutral: 'bg-black-600 text-white',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
);

const iconWrapperStyles = cva('', {
  variants: {
    variant: {
      info: ' text-blue-700',
      success: 'text-green-700',
      warning: 'text-yellow-700',
      danger: 'text-red-700',
      neutral: 'text-black-700',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

const titleStyles = cva('text-sm font-semibold leading-5', {
  variants: {
    variant: {
      info: 'text-blue-900',
      success: 'text-green-900',
      warning: 'text-yellow-900',
      danger: 'text-red-700',
      neutral: 'text-black-900',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

const descriptionStyles = cva('text-sm leading-6', {
  variants: {
    variant: {
      info: 'text-blue-700',
      success: 'text-green-700',
      warning: 'text-yellow-800',
      danger: 'text-red-700',
      neutral: 'text-black-600',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

const actionButtonStyles = cva(
  'inline-flex items-center cursor-pointer justify-center gap-2 rounded-lg text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
  {
    variants: {
      intent: {
        primary:
          'bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 active:bg-blue-800 focus-visible:outline-blue-200',
        secondary:
          'border border-black-300 px-4 py-2 text-black-700 hover:bg-black-50 active:bg-white focus-visible:outline-black-200',
        tertiary:
          'px-0 py-0 text-blue-700 hover:text-blue-800 underline-offset-4 hover:underline focus-visible:outline-blue-200',
      },
    },
    defaultVariants: {
      intent: 'primary',
    },
  },
);

type AlertVariant = NonNullable<
  VariantProps<typeof alertContainerStyles>['variant']
>;

type ActionIntent = NonNullable<
  VariantProps<typeof actionButtonStyles>['intent']
>;

type BaseAction = {
  label: ReactNode;
  icon?: ReactNode;
  intent?: ActionIntent;
  className?: string;
};

type ClickAction = BaseAction & {
  onClick: () => void;
  href?: never;
};

type LinkAction = BaseAction & {
  href: string;
  target?: string;
  rel?: string;
  onClick?: never;
};

export type AlertAction = ClickAction | LinkAction;

export interface AlertProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /**
   * Main heading for the alert. Shown prominently next to the icon.
   */
  title: ReactNode;
  /**
   * Optional supporting copy displayed beneath the title.
   */
  description?: ReactNode;
  /**
   * Replace the default variant icon with a custom node.
   */
  icon?: ReactNode;
  /**
   * Configure stacked or inline actions rendered beneath the content.
   */
  actions?: AlertAction[];
  /**
   * Enable the close affordance. If true, provide `onDismiss` to handle the click.
   */
  dismissible?: boolean;
  /**
   * Handler fired when the dismiss control is triggered.
   */
  onDismiss?: () => void;
  /**
   * Visual tone of the alert. Defaults to "info".
   */
  variant?: AlertVariant;
  /**
   * When provided, renders expandable content using the component's children.
   */
  expandable?: boolean;
  /**
   * Content rendered when the alert is expanded.
   */
  children?: ReactNode;
  /**
   * Sets the initial expanded state when the component manages its own state.
   */
  defaultExpanded?: boolean;
  /**
   * Controls the expanded state.
   */
  expanded?: boolean;
  /**
   * Callback fired whenever the expanded state changes.
   */
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * Label displayed when the expandable section is collapsed.
   */
  expandLabel?: ReactNode;
  /**
   * Label displayed when the expandable section is open.
   */
  collapseLabel?: ReactNode;
  /**
   * Visual intent for the expand toggle action. Defaults to `tertiary`.
   */
  expandActionIntent?: ActionIntent;

  /**
   * Whether to show a more detailed indicator alert with multiple labels.
   * This can be used to provide additional context or information to the user.
   */
  detailed?: boolean;

  /**
   * Label for the detailed indicator. Only used if `detailed` is true.
   * This label is displayed alongside the detailed indicator to provide context.
   */
  detailedIndicatorLabel?: string;
}

const variantIconMap: Partial<Record<AlertVariant, ReactNode>> = {
  info: <InfoIcon className="h-5 w-5" />,
  danger: <InfoIcon className="h-5 w-5" />,
  success: <CheckIcon className="h-5 w-5" />,
  warning: <AlertCircleIcon className="h-5 w-5" />,
  neutral: <InfoIcon className="h-5 w-5" />,
};

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  {
    variant = 'info',
    title,
    description,
    icon,
    actions,
    dismissible = false,
    onDismiss,
    className,
    expandable,
    children,
    defaultExpanded = false,
    expanded,
    onExpandedChange,
    expandLabel = 'Learn more',
    collapseLabel = 'Hide',
    expandActionIntent = 'tertiary',
    detailed = false,
    detailedIndicatorLabel,
    ...rest
  }: AlertProps,
  ref,
) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  useEffect(() => {
    if (expanded === undefined) {
      setInternalExpanded(defaultExpanded);
    }
  }, [defaultExpanded, expanded]);

  const isExpandable = expandable ?? Boolean(children);
  const expandedState = expanded ?? internalExpanded;
  const collapsibleId = useId();
  const hasCustomActions = Array.isArray(actions) && actions.length > 0;
  const shouldRenderActions = hasCustomActions || isExpandable || dismissible;

  const toggleExpanded = () => {
    const nextState = !expandedState;
    onExpandedChange?.(nextState);

    if (expanded === undefined) {
      setInternalExpanded(nextState);
    }
  };

  return (
    <div
      role="alert"
      ref={ref}
      className={twMerge(alertContainerStyles({ variant }), className)}
      {...rest}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-1 flex-row items-center justify-center gap-3">
          {icon !== null && (
            <span className={iconWrapperStyles({ variant })} aria-hidden>
              {variantIconMap[variant]}
            </span>
          )}
          {detailed && detailedIndicatorLabel && (
            <div className={detailedViewWrapperStyles({ variant })}>
              <div className={detailedViewLabelWrapperStyles({ variant })}>
                {detailedIndicatorLabel}
              </div>
              <div className="text-sm text-blue-900">{title}</div>
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                {!detailed && (
                  <p className={titleStyles({ variant })}>{title}</p>
                )}
              </div>
              {shouldRenderActions ? (
                <div className="flex flex-wrap items-center gap-2">
                  {actions?.map(
                    (
                      {
                        label,
                        icon: actionIcon,
                        intent = 'primary',
                        className: actionClassName,
                        ...action
                      },
                      index,
                    ) => {
                      const key = `action-${index}`;
                      const classes = twMerge(
                        actionButtonStyles({ intent }),
                        actionClassName,
                      );

                      if ('href' in action && action.href) {
                        const rel =
                          action.target === '_blank'
                            ? (action.rel ?? 'noreferrer noopener')
                            : action.rel;
                        return (
                          <a
                            key={key}
                            href={action.href}
                            target={action.target}
                            rel={rel}
                            className={classes}
                          >
                            {actionIcon}
                            {label}
                          </a>
                        );
                      }

                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={action.onClick}
                          className={classes}
                        >
                          {actionIcon}
                          {label}
                        </button>
                      );
                    },
                  )}
                  {isExpandable ? (
                    <button
                      type="button"
                      onClick={toggleExpanded}
                      className={twMerge(
                        actionButtonStyles({ intent: expandActionIntent }),
                      )}
                      aria-expanded={expandedState}
                      aria-controls={collapsibleId}
                    >
                      <Typography
                        variant="body-sm"
                        as="span"
                        weight="medium"
                        className="!text-blue-700"
                      >
                        {expandedState ? collapseLabel : expandLabel}
                      </Typography>
                      <ChevronBottomIcon
                        className={`ml-1 size-3 stroke-blue-700 transition duration-100 ease-out ${
                          expandedState ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  ) : null}

                  {dismissible ? (
                    <button
                      type="button"
                      onClick={onDismiss}
                      className="hover:bg-black-100 focus-visible:outline-black-200 ml-auto flex h-8 w-8 items-center justify-center rounded-md text-inherit transition focus-visible:outline-2 focus-visible:outline-offset-2"
                      aria-label="Dismiss alert"
                    >
                      <CloseIcon className="stroke-grey-700 h-3 w-3" />
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            {description ? (
              <div className={twMerge('mt-1', descriptionStyles({ variant }))}>
                {description}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {isExpandable ? (
        <div
          id={collapsibleId}
          data-state={expandedState ? 'open' : 'closed'}
          className={twMerge(
            'grid transition-[grid-template-rows] duration-300 ease-in-out',
            expandedState ? 'mt-2 grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div
            className={twMerge(
              'overflow-hidden transition-opacity duration-300 ease-in-out',
              expandedState ? 'opacity-100' : 'opacity-0',
            )}
          >
            <div className="pt-2">{children}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
});

export default Alert;
